import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { RecommendItem } from '@/api/types.gen'
import { getApiV1TukuRecommend } from '@/api/sdk.gen'
import { isValidImageUrl } from '@/utils/image-url'

export const GALLERY_PAGE_SIZE = 8

/**
 * 图库推荐瀑布流分页 store 工厂。
 * 首页与图库 Tab 页各持有独立实例，分页/加载状态互不干扰。
 */
function createGalleryFeedStore(id: string) {
  return defineStore(id, () => {
    const gallery = ref<RecommendItem[]>([])
    const galleryPage = ref(1)
    const galleryHasMore = ref(true)
    const galleryLoading = ref(false)
    /** 当前列表所属地区；与请求地区不一致时先清空重载（#250） */
    const galleryRegion = ref<string | null>(null)
    /** 请求序号：地区切换/重置后旧响应作废，避免旧地区数据回写 */
    let requestSeq = 0

    async function loadMoreGallery(region: string) {
      if (galleryRegion.value !== region) {
        resetGallery()
        galleryRegion.value = region
      }
      if (galleryLoading.value || !galleryHasMore.value) return
      const seq = ++requestSeq
      galleryLoading.value = true
      try {
        const res = await getApiV1TukuRecommend({
          query: { page: galleryPage.value, size: GALLERY_PAGE_SIZE, region },
        })
        // 期间已切换地区或重置：丢弃本次响应
        if (seq !== requestSeq) return
        const list = res.data?.data?.list ?? []
        // 图片链接缺失/非图片后缀的记录整条丢弃，不渲染卡片和标题（#249）
        const valid = list.filter((item) => isValidImageUrl(item.pictureUrl))
        gallery.value = [...gallery.value, ...valid]
        // 分页是否结束以服务端原始条数判断，避免整页被过滤后提前结束
        galleryHasMore.value = list.length >= GALLERY_PAGE_SIZE
        galleryPage.value++
        // 当前页全部被过滤但服务端还有数据时，继续拉下一页，避免列表卡住
        if (valid.length === 0 && galleryHasMore.value) {
          galleryLoading.value = false
          await loadMoreGallery(region)
        }
      } finally {
        if (seq === requestSeq) galleryLoading.value = false
      }
    }

    function resetGallery() {
      requestSeq++
      gallery.value = []
      galleryPage.value = 1
      galleryHasMore.value = true
      galleryLoading.value = false
    }

    return {
      gallery,
      galleryPage,
      galleryHasMore,
      galleryLoading,
      galleryRegion,
      loadMoreGallery,
      resetGallery,
    }
  })
}

/** 首页图库区实例（沿用原 home-store 的数据形态） */
export const useHomeGalleryStore = createGalleryFeedStore('home-gallery-store')

/** 底部「图库」Tab 页实例 */
export const useFindGalleryStore = createGalleryFeedStore('find-gallery-store')

/** 高手之家论坛 Tab 的图库胶囊实例 */
export const useGszjGalleryStore = createGalleryFeedStore('gszj-gallery-store')
